import { Box, IconButton, Skeleton, Typography, useTheme } from '@mui/material';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';
import type { Annotation, Span } from '@verantos-dev/core-annotation-tool';
import { Icon } from '@verantos-dev/shared-components';

import {
  addHighlightedTextSpanFromSpan,
  removeHighlightedTextSpan,
  setSelectedNoteId,
  removePendingSpan,
  clearHighlightedTextSpans,
  addToEvidenceIdsToDelete,
  triggerScrollToHighlight,
} from '../../state/annotationSlice';
import {
  selectSelectedNoteId,
  selectPendingSpansForAnnotation,
  selectConceptAnnotationIdsInEditMode,
  selectEvidenceIdsToDelete,
} from '../../state/selector';

interface ShowEvidencesListProps {
  annotation: Annotation | null;
  conceptId: string;
  editEnabled?: boolean;
  editingAnnotationId?: string;
  loading?: boolean;
}

export const ShowEvidencesList = ({
  annotation,
  conceptId,
  editingAnnotationId,
  loading = false,
  editEnabled = false,
}: ShowEvidencesListProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const selectedNoteId = useAppSelector(selectSelectedNoteId);

  // For new annotations, use temporary annotationId; for existing, use actual annotationId
  const annotationId = annotation?.annotationId || editingAnnotationId || `new-${conceptId}`;

  // Memoize the selector to avoid recreating it on every render
  const pendingSpansSelector = useMemo(
    () => selectPendingSpansForAnnotation(annotationId),
    [annotationId]
  );

  const conceptAnnotationIdsInEdit = useAppSelector(selectConceptAnnotationIdsInEditMode);

  // Get pending spans (not yet saved) for this annotation
  const pendingSpans = useAppSelector(pendingSpansSelector);

  const evidenceIdsToDeleteMap = useAppSelector(selectEvidenceIdsToDelete);
  const evidenceIdsToDelete = evidenceIdsToDeleteMap[annotationId] || [];

  // Combine saved spans (from server) and pending spans (not yet saved)
  // For new annotations, there are no saved spans yet
  const allSpans: Array<Span & { isPending?: boolean }> = [
    ...(annotation?.spans
      .filter((span) => !evidenceIdsToDelete.includes(span.evidenceId!))
      .map((span) => ({ ...span, isPending: false })) || []),
    ...pendingSpans.map((span) => ({ ...span, isPending: true })),
  ];

  const showHighlightedText = (span: Span, number: number, spanAnnotationId: string) => {
    dispatch(setSelectedNoteId(span.noteId));
    dispatch(clearHighlightedTextSpans());
    dispatch(
      addHighlightedTextSpanFromSpan({
        span,
        number,
        annotationId: spanAnnotationId,
      })
    );
    dispatch(triggerScrollToHighlight());
  };

  const showPendingSpan = (noteId: string) => {
    dispatch(setSelectedNoteId(noteId));
    dispatch(triggerScrollToHighlight());
  };

  const handleDeleteSpan = (
    e: React.MouseEvent<HTMLButtonElement>,
    spanAnnotationId: string,
    evidenceId: string,
    noteId: string,
    offsetStart: number,
    offsetEnd: number,
    isPending: boolean
  ) => {
    e.preventDefault();

    if (isPending) {
      dispatch(
        removePendingSpan({ annotationId: spanAnnotationId, noteId, offsetStart, offsetEnd })
      );
      if (selectedNoteId === noteId) {
        dispatch(
          removeHighlightedTextSpan({
            offsetStart,
            offsetEnd,
            noteId,
            annotationId: spanAnnotationId,
          })
        );
      }
      return;
    }

    // Soft-delete: add to evidenceIdsToDelete and remove highlight
    dispatch(addToEvidenceIdsToDelete({ annotationId: spanAnnotationId, evidenceId }));
    if (selectedNoteId === noteId) {
      dispatch(
        removeHighlightedTextSpan({
          offsetStart,
          offsetEnd,
          noteId,
          annotationId: spanAnnotationId,
        })
      );
    }
  };

  return (
    <Box id="saved-evidence-container">
      <ul id="saved-evidence-list" style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {allSpans.map((span, spanIndex: number) => {
          const isPending = span.isPending ?? false;
          if (loading) {
            return (
              <li
                key={`${span.noteId}-${span.offsetStart}-${span.offsetEnd}-${
                  isPending ? 'pending' : 'saved'
                }`}
              >
                <Skeleton variant="text" width="100%" height="50px" />
              </li>
            );
          }
          return (
            <li
              key={`${span.noteId}-${span.offsetStart}-${span.offsetEnd}-${
                isPending ? 'pending' : 'saved'
              }`}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  py: theme.spacing(1),
                  gap: theme.spacing(3),
                }}
              >
                <Box sx={{ textOverflow: 'ellipsis', overflow: 'hidden', flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    noWrap
                    sx={{
                      fontStyle: isPending ? 'italic' : 'normal',
                      color: isPending ? theme.palette.text.secondary : 'inherit',
                    }}
                  >
                    "{span.text}" {isPending && '(Pending)'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                  {isPending ? (
                    <Typography
                      onClick={() => showPendingSpan(span.noteId)}
                      variant="body2"
                      sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                    >
                      {formatMessage({
                        id: 'workspaceCommon.savedAnnotationCard.showEvidenceButton',
                      })}
                    </Typography>
                  ) : (
                    <Typography
                      onClick={() => showHighlightedText(span, spanIndex + 1, annotationId)}
                      variant="body2"
                      sx={{ color: theme.palette.primary.main, cursor: 'pointer' }}
                    >
                      {formatMessage({
                        id: 'workspaceCommon.savedAnnotationCard.showEvidenceButton',
                      })}
                    </Typography>
                  )}
                  {editEnabled && (
                    <IconButton
                      onClick={(e) =>
                        handleDeleteSpan(
                          e,
                          annotationId,
                          span.evidenceId!,
                          span.noteId,
                          span.offsetStart,
                          span.offsetEnd,
                          isPending
                        )
                      }
                    >
                      <Icon icon="clear" id="delete-evidence-icon" width={12} height={12} />
                    </IconButton>
                  )}
                </Box>
              </Box>
            </li>
          );
        })}
      </ul>
    </Box>
  );
};

export default ShowEvidencesList;
